import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { RhythmCatalog, RhythmCatalogEntry } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import { useImprovClient } from '../hooks/useImprovClient.js';

/**
 * Rhythm-catalog view (M5, §20.9): list device rhythm catalogs and their entries, add a catalog,
 * import verified entries from a pasted JSON array, and archive/unarchive a catalog.
 *
 * §20.8 safety: seed ONLY verified device data. Never fabricate RC-600 pattern or kit names — import
 * only entries you can confirm against the real device catalog, an artist-defined entry, or a
 * verified import.
 */

interface CatalogDraft {
  name: string;
  deviceProfileKey: string;
}

const EMPTY_DRAFT: CatalogDraft = { name: '', deviceProfileKey: 'boss-rc600' };

function meterText(entry: RhythmCatalogEntry): string {
  return `${entry.meter.numerator}/${entry.meter.denominator}`;
}

export function RhythmCatalogPage() {
  const { artistId = '' } = useParams();
  const client = useImprovClient();

  const [catalogs, setCatalogs] = useState<RhythmCatalog[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState<CatalogDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Per-catalog import textarea state, keyed by catalog id.
  const [importText, setImportText] = useState<Record<string, string>>({});
  const [importError, setImportError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setCatalogs(await client.listCatalogs(artistId));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load catalogs.');
    } finally {
      setLoading(false);
    }
  }, [client, artistId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!draft.name.trim() || !draft.deviceProfileKey.trim()) return;
      setSaving(true);
      setFormError(null);
      setNotice(null);
      try {
        const created = await client.createCatalog(artistId, {
          name: draft.name.trim(),
          deviceProfileKey: draft.deviceProfileKey.trim(),
        });
        setCatalogs((prev) => [created, ...prev]);
        setNotice(`Added “${created.name}”.`);
        setDraft(EMPTY_DRAFT);
      } catch (err) {
        if (err instanceof ApiError) setFormError(err);
        else setFormError(null);
      } finally {
        setSaving(false);
      }
    },
    [client, artistId, draft],
  );

  const onToggleArchive = useCallback(
    async (catalog: RhythmCatalog) => {
      const next = catalog.status === 'archived' ? 'active' : 'archived';
      setBusyId(catalog.rhythmCatalogId);
      setNotice(null);
      try {
        const updated = await client.updateCatalog(catalog.rhythmCatalogId, { status: next });
        setCatalogs((prev) =>
          prev.map((c) => (c.rhythmCatalogId === updated.rhythmCatalogId ? updated : c)),
        );
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : 'Could not update catalog.');
      } finally {
        setBusyId(null);
      }
    },
    [client],
  );

  const onImport = useCallback(
    async (catalog: RhythmCatalog) => {
      const raw = importText[catalog.rhythmCatalogId]?.trim() ?? '';
      if (!raw) return;
      let entries: RhythmCatalogEntry[];
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) throw new Error('Expected a JSON array of entries.');
        entries = parsed as RhythmCatalogEntry[];
      } catch (err) {
        setImportError((prev) => ({
          ...prev,
          [catalog.rhythmCatalogId]: err instanceof Error ? err.message : 'Invalid JSON.',
        }));
        return;
      }
      setBusyId(catalog.rhythmCatalogId);
      setImportError((prev) => ({ ...prev, [catalog.rhythmCatalogId]: '' }));
      setNotice(null);
      try {
        const result = await client.importCatalogEntries(catalog.rhythmCatalogId, entries);
        setCatalogs((prev) =>
          prev.map((c) =>
            c.rhythmCatalogId === result.catalog.rhythmCatalogId ? result.catalog : c,
          ),
        );
        setNotice(`Imported ${result.imported} entr${result.imported === 1 ? 'y' : 'ies'}.`);
        setImportText((prev) => ({ ...prev, [catalog.rhythmCatalogId]: '' }));
      } catch (err) {
        setImportError((prev) => ({
          ...prev,
          [catalog.rhythmCatalogId]:
            err instanceof ApiError ? err.message : 'Import failed.',
        }));
      } finally {
        setBusyId(null);
      }
    },
    [client, importText],
  );

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Rhythm catalogs
          </p>
          <h1>Rhythm catalogs</h1>
          <p className="muted">
            Device rhythm catalogs map general grooves to exact patterns, variations, and kits.
          </p>
        </div>
      </header>

      {loadError && <p className="error">{loadError}</p>}

      <section className="card">
        <div className="module-head">
          <h2>New catalog</h2>
          {notice && (
            <span className="muted" aria-live="polite">
              {notice}
            </span>
          )}
        </div>
        <form className="catalog-form" onSubmit={onCreate}>
          <label>
            Name
            <input
              value={draft.name}
              required
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. RC-600 verified rock"
            />
          </label>
          <label>
            Device profile key
            <input
              value={draft.deviceProfileKey}
              required
              onChange={(e) => setDraft((d) => ({ ...d, deviceProfileKey: e.target.value }))}
              placeholder="boss-rc600"
              spellCheck={false}
              autoCapitalize="none"
            />
          </label>
          <div className="button-row">
            <button
              type="submit"
              className="action--primary"
              disabled={saving || !draft.name.trim()}
            >
              Add catalog
            </button>
          </div>
          {formError && (
            <div className="validation" role="alert">
              <p className="error">{formError.message}</p>
              {formError.fieldErrors.length > 0 && (
                <ul>
                  {formError.fieldErrors.map((fe, i) => (
                    <li key={`${fe.path}-${i}`}>
                      <code>{fe.path}</code>: {fe.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </section>

      <section className="card">
        <h2>Catalogs</h2>
        {catalogs.length === 0 && !loading ? (
          <p className="muted">No catalogs yet. Add one above.</p>
        ) : (
          <ul className="catalog-list">
            {catalogs.map((catalog) => (
              <li key={catalog.rhythmCatalogId} className="catalog-card">
                <div className="catalog-card__head">
                  <div>
                    <h3 className="catalog-card__name">{catalog.name}</h3>
                    <p className="muted catalog-card__meta">
                      {catalog.deviceProfileKey} · v{catalog.catalogVersion} · {catalog.source} ·{' '}
                      {catalog.entries.length} entr{catalog.entries.length === 1 ? 'y' : 'ies'}
                    </p>
                  </div>
                  <span className={`health-badge health-badge--${catalog.status}`}>
                    {catalog.status}
                  </span>
                </div>

                {catalog.entries.length > 0 && (
                  <div className="deliveries-scroll">
                    <table className="deliveries-table">
                      <thead>
                        <tr>
                          <th>Display name</th>
                          <th>Family</th>
                          <th>Meter</th>
                          <th>Tempo</th>
                          <th>Variations</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.entries.map((entry) => (
                          <tr key={entry.catalogEntryId}>
                            <td>{entry.displayName}</td>
                            <td>{entry.family}</td>
                            <td>{meterText(entry)}</td>
                            <td>
                              {entry.recommendedTempo.min}–{entry.recommendedTempo.max}
                            </td>
                            <td>{entry.variations.join(', ') || '—'}</td>
                            <td>{entry.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <details className="catalog-import">
                  <summary>Import entries (JSON)</summary>
                  <p className="hint catalog-import__warn">
                    Seed only VERIFIED device data. Never fabricate RC-600 pattern or kit names
                    (§20.8).
                  </p>
                  <label>
                    Paste a JSON array of RhythmCatalogEntry
                    <textarea
                      rows={6}
                      value={importText[catalog.rhythmCatalogId] ?? ''}
                      onChange={(e) =>
                        setImportText((prev) => ({
                          ...prev,
                          [catalog.rhythmCatalogId]: e.target.value,
                        }))
                      }
                      placeholder='[{"catalogEntryId":"rhythm_…","deviceProfileKey":"boss-rc600","displayName":"…","family":"rock","meter":{"numerator":4,"denominator":4},"recommendedTempo":{"min":84,"max":132},"variations":["A","B"],"source":"verified_device_catalog"}]'
                      spellCheck={false}
                    />
                  </label>
                  {importError[catalog.rhythmCatalogId] && (
                    <p className="error">{importError[catalog.rhythmCatalogId]}</p>
                  )}
                  <div className="button-row">
                    <button
                      type="button"
                      onClick={() => void onImport(catalog)}
                      disabled={
                        busyId === catalog.rhythmCatalogId ||
                        !(importText[catalog.rhythmCatalogId]?.trim() ?? '')
                      }
                    >
                      Import
                    </button>
                  </div>
                </details>

                <div className="button-row">
                  <button
                    type="button"
                    className={catalog.status === 'archived' ? 'action--primary' : 'ghost'}
                    onClick={() => void onToggleArchive(catalog)}
                    disabled={busyId === catalog.rhythmCatalogId}
                  >
                    {catalog.status === 'archived' ? 'Unarchive' : 'Archive'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <p aria-busy="true">Loading…</p>}
      </section>
    </main>
  );
}
